import { usePublicClient } from 'wagmi';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../configs/queryKeys';
import { ERC20ABI } from '../components/abi/ERC20ABI';
import { formatUnits, parseAbi } from 'viem';
import { UniswapV3OracleABI } from '../components/abi/UniswapV3OracleABI';
import { DynaVaultABI } from '../components/abi/DynavaultABI';
import { DynaLiquidityABI } from '../components/abi/DynaLiquidityABI';
import BigNumber from 'bignumber.js';
import { getOracleDeployment } from 'sfi-oracle-sdk';

export interface QueryOptions {
  query?: {
    staleTime?: number;
    enabled?: boolean;
  };
}

interface UseVaultTokensQueryParams extends QueryOptions {
  vaultChainId: number;
  vaultAddress: `0x${string}`;
  from: `0x${string}`;
}

export const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000';

const buildPriceCall = (params: {
  chainId: number;
  tokenAddress: `0x${string}`;
  oracleAddress: `0x${string}`;
}) => {
  const { chainId, tokenAddress, oracleAddress } = params;
  const oracleDeployment = getOracleDeployment(chainId);
  if (!oracleDeployment) {
    throw new Error(`No oracle deployment found for chainId ${chainId}`);
  }

  return {
    chainId,
    abi: UniswapV3OracleABI,
    functionName: 'getPrice',
    address: oracleAddress,
    args: [tokenAddress, oracleDeployment.referenceAsset],
  } as const;
};

const getReferenceAssetDecimals = async (
  publicClient: NonNullable<ReturnType<typeof usePublicClient>>,
  chainId: number
) => {
  const oracleDeployment = getOracleDeployment(chainId);
  if (!oracleDeployment) {
    throw new Error(`No oracle deployment found for chainId ${chainId}`);
  }

  return publicClient.readContract({
    abi: ERC20ABI,
    address: oracleDeployment.referenceAsset,
    functionName: 'decimals',
    authorizationList: undefined,
  });
};

export const useVaultTokensQuery = (params: UseVaultTokensQueryParams) => {
  const { vaultChainId, vaultAddress, from, query } = params;

  const publicClient = usePublicClient({
    chainId: vaultChainId,
  });

  const useQueryOptions = {
    queryKey: queryKeys.vaultTokens({ vaultAddress, vaultChainId }),
    ...query,
  };

  return useQuery({
    ...useQueryOptions,
    refetchInterval: query?.staleTime || 30000,
    queryFn: async () => {
      if (!publicClient) {
        throw new Error('Public client is not available');
      }

      const snapshot = await publicClient.readContract({
        abi: DynaVaultABI,
        account: from,
        address: vaultAddress,
        functionName: 'takeSnapshot',
        authorizationList: undefined,
      });

      const symbolCalls = snapshot.tokens.map((token) => ({
        address: token.tokenAddress,
        abi: ERC20ABI,
        account: from,
        chainId: vaultChainId,
        functionName: 'symbol',
        args: [],
      }));

      const decimalCalls = snapshot.tokens.map((token) => ({
        address: token.tokenAddress,
        abi: ERC20ABI,
        account: from,
        chainId: vaultChainId,
        functionName: 'decimals',
        args: [],
      }));

      const [symbolList, decimalList] = await Promise.all([
        publicClient.multicall({
          contracts: symbolCalls,
          authorizationList: undefined,
        }),
        publicClient.multicall({
          contracts: decimalCalls,
          authorizationList: undefined,
        }),
      ]);

      const assetsOracleAddress = await publicClient.readContract({
        abi: DynaVaultABI,
        address: vaultAddress,
        account: from,
        functionName: 'referenceAssetOracle',
        authorizationList: undefined,
      });

      const priceCalls = snapshot.tokens.map((token) =>
        buildPriceCall({
          chainId: vaultChainId,
          oracleAddress: assetsOracleAddress,
          tokenAddress: token.tokenAddress,
        })
      );

      const [priceList, referenceAssetDecimals] = await Promise.all([
        publicClient.multicall({
          contracts: priceCalls,
        }),
        getReferenceAssetDecimals(publicClient, vaultChainId),
      ]);

      const totalDepositDept = snapshot.tokens.reduce(
        (acc, token) => acc + BigInt(token.depositDebt),
        BigInt(0)
      );

      return snapshot.tokens.map((token, index) => {
        const decimals = Number(decimalList[index]?.result) || 1;
        const symbol = String(symbolList[index]?.result) || 'UNK';
        const price =
          priceList[index] && priceList[index].result
            ? Number(
                formatUnits(
                  priceList[index].result![0],
                  Number(referenceAssetDecimals)
                )
              )
            : 0;
        const idleInUsd =
          price * parseFloat(formatUnits(token.balance, decimals));

        return {
          ...token,
          idleInUsd,
          symbol,
          decimals,
          price,
          currentWeight:
            (Number(token.depositDebt) / Number(totalDepositDept)) * 100,
          targetWeight:
            (Number(token.depositDebtRatio) / Number(BigInt(1e18))) * 100,
        };
      });
    },
  });
};

export const useDynaLiquidityTokensQuery = (
  params: UseVaultTokensQueryParams & {
    asset: `0x${string}`;
    dynaLiquidity: `0x${string}`;
  }
) => {
  const { vaultChainId, vaultAddress, from, query, dynaLiquidity, asset } =
    params;

  const publicClient = usePublicClient({
    chainId: vaultChainId,
  });

  const useQueryOptions = {
    queryKey: queryKeys.vaultTokens({ vaultAddress, vaultChainId }),
    ...query,
  };

  return useQuery({
    ...useQueryOptions,
    refetchInterval: query?.staleTime || 30000,
    queryFn: async () => {
      if (!publicClient) {
        throw new Error('Public client is not available');
      }

      const liquidityABI = parseAbi([
        'function getActualSupply() external view returns (uint256)',
      ]);

      const totalAssets = await publicClient.readContract({
        abi: DynaVaultABI,
        account: from,
        address: vaultAddress,
        functionName: 'totalAssets',
        authorizationList: undefined,
      });

      const vaultDecimals = await publicClient.readContract({
        abi: DynaVaultABI,
        account: from,
        address: vaultAddress,
        functionName: 'decimals',
        authorizationList: undefined,
      });

      const liquidityTokensSupply = await publicClient.readContract({
        abi: liquidityABI,
        account: from,
        address: asset,
        functionName: 'getActualSupply',
        authorizationList: undefined,
      });

      const tokensAndBalances = await publicClient.readContract({
        abi: DynaLiquidityABI,
        account: from,
        address: dynaLiquidity,
        functionName: 'tokensAndBalances',
        authorizationList: undefined,
      });

      const snapshot = tokensAndBalances[0]
        .map((token, index) => {
          if (index === 0) {
            return null;
          }

          const balance = tokensAndBalances[1][index].toString();

          const availableFunds = new BigNumber(balance)
            .dividedBy(
              new BigNumber(
                formatUnits(liquidityTokensSupply, vaultDecimals).toString()
              )
            )
            .multipliedBy(formatUnits(totalAssets, vaultDecimals).toString())
            .toFixed(0);

          return {
            tokenAddress: token,
            balance: availableFunds,
          };
        })
        .filter((token) => token !== null);

      const symbolCalls = snapshot.map((token) => ({
        address: token.tokenAddress,
        abi: ERC20ABI,
        account: from,
        chainId: vaultChainId,
        functionName: 'symbol',
        args: [],
      }));

      const decimalCalls = snapshot.map((token) => ({
        address: token.tokenAddress,
        abi: ERC20ABI,
        account: from,
        chainId: vaultChainId,
        functionName: 'decimals',
        args: [],
      }));

      const [symbolList, decimalList] = await Promise.all([
        publicClient.multicall({
          contracts: symbolCalls,
          authorizationList: undefined,
        }),
        publicClient.multicall({
          contracts: decimalCalls,
          authorizationList: undefined,
        }),
      ]);

      const assetsOracleAddress = await publicClient.readContract({
        abi: DynaVaultABI,
        address: vaultAddress,
        account: from,
        functionName: 'referenceAssetOracle',
        authorizationList: undefined,
      });

      const priceCalls = snapshot.map((token) =>
        buildPriceCall({
          chainId: vaultChainId,
          oracleAddress: assetsOracleAddress,
          tokenAddress: token.tokenAddress,
        })
      );

      const [priceList, referenceAssetDecimals] = await Promise.all([
        publicClient.multicall({
          contracts: priceCalls,
          authorizationList: undefined,
        }),
        getReferenceAssetDecimals(publicClient, vaultChainId),
      ]);

      const tokensWithUsdValue = snapshot.map((token, index) => {
        const decimals = Number(decimalList[index]?.result) || 1;
        const symbol = String(symbolList[index]?.result) || 'UNK';

        const price =
          priceList[index] && priceList[index].result
            ? Number(
                formatUnits(
                  priceList[index].result![0],
                  Number(referenceAssetDecimals)
                )
              )
            : 0;

        const idleInUsd =
          price * parseFloat(formatUnits(BigInt(token.balance), decimals));

        return {
          ...token,
          idleInUsd,
          symbol,
          decimals,
          price,
        };
      });

      const totalUsdValue = tokensWithUsdValue.reduce(
        (acc, token) => acc.plus(new BigNumber(token.idleInUsd)),
        new BigNumber(0)
      );

      return tokensWithUsdValue.map((token) => ({
        ...token,
        currentWeight: new BigNumber(token.idleInUsd)
          .dividedBy(totalUsdValue)
          .multipliedBy(100)
          .toNumber(),
      }));
    },
  });
};

interface UseVaultsAddressesParams extends QueryOptions {
  chainId: number;
  vaultAddress: `0x${string}`;
  from?: `0x${string}`;
}

export const useVaultsAddresses = (params: UseVaultsAddressesParams) => {
  const { chainId, vaultAddress, query, from } = params;

  const publicClient = usePublicClient({
    chainId,
  });

  const useQueryOptions = {
    queryKey: queryKeys.vaultAddresses({
      vaultAddress,
      chainId,
    }),
    ...query,
    enable: query?.enabled && Boolean(from),
  };

  return useQuery({
    ...useQueryOptions,
    queryFn: async () => {
      if (!publicClient) {
        throw new Error('Public client is not available');
      }

      const managerAddress = await publicClient.readContract({
        abi: DynaVaultABI,
        account: from,
        address: vaultAddress,
        functionName: 'manager',
        authorizationList: undefined,
      });

      const routerRegistry = await publicClient.readContract({
        abi: DynaVaultABI,
        account: from,
        address: vaultAddress,
        functionName: 'routerRegistry',
        authorizationList: undefined,
      });

      const vaultZapper = await publicClient.readContract({
        abi: parseAbi([
          'function getDefaultTokenRouter(address token) view returns (address)',
        ]),
        account: from,
        address: routerRegistry,
        functionName: 'getDefaultTokenRouter',
        args: [vaultAddress],
        authorizationList: undefined,
      });

      return { managerAddress, routerRegistry, vaultZapper };
    },
  });
};
